import { makeStyles } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Skeleton, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { cardTitle } from "assets/jss/material-dashboard-pro-react";
import Card from "components/UI/Card/Card";
import CardBody from "components/UI/Card/CardBody";
import CardHeader from "components/UI/Card/CardHeader";
import CardIcon from "components/UI/Card/CardIcon";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import GridItemTS from "components/UI/Grid/GridItemTS";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { ProfileTab } from "enums/ProfileTabs";
import { WorkItemProfiles } from "helpers/profileEnum";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PaginatedResult } from "services/@types";
import {
  useDashboardAtvsRequiringActionByRole,
  prefetchData,
} from "services/input-core/dashboard/get-dashboard-atvs-requiring-action-by-role";
import { ActivityItemWorker } from "services/input-core/work-items/@types";
import { useSubscription } from "store/auth/auth-store";
import { ProfileEnum } from "utils/@types";
import { getBadgeFromStatus, getFilters, getSorting } from "utils/functions";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
};

const useStyles = makeStyles(styles as never);

export function LatestPendingAnalysis() {
  const classes = useStyles();
  const { t } = useTranslation("dashboard");
  const { isKuo, subscription } = useSubscription();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tableData, setTableData] = useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const { data, isLoading, isFetching } = useDashboardAtvsRequiringActionByRole(
    "supplier",
    {
      "page[size]": tableData.pageSize,
      "page[index]": tableData.pageIndex,
      "filter[activityItemStatus]": "Pending",
      ...getFilters(tableData.columnFilters),
      ...getSorting(
        tableData.sorting.length === 0
          ? [
              {
                id: "fieldglassId",
                desc: true,
              },
            ]
          : tableData.sorting,
      ),
    },
    {
      staleTime: 1000 * 60 * 5,
      onSuccess: (data: PaginatedResult<ActivityItemWorker>) => {
        prefetchData(
          queryClient,
          "supplier",
          {
            "page[size]": tableData.pageSize,
            "page[index]": tableData.pageIndex + 1,
            "filter[activityItemStatus]": "Pending",
            ...getFilters(tableData.columnFilters),
            ...getSorting(
              tableData.sorting.length === 0
                ? [
                    {
                      id: "fieldglassId",
                      desc: true,
                    },
                  ]
                : tableData.sorting,
            ),
          },
          subscription?.subscriptionId,
        );
        prefetchData(
          queryClient,
          "supplier",
          {
            "page[size]": tableData.pageSize,
            "page[index]":
              tableData.pageIndex > 0 ? tableData.pageIndex - 1 : 0,
            "filter[activityItemStatus]": "Pending",
            ...getFilters(tableData.columnFilters),
            ...getSorting(
              tableData.sorting.length === 0
                ? [
                    {
                      id: "fieldglassId",
                      desc: true,
                    },
                  ]
                : tableData.sorting,
            ),
          },
          subscription?.subscriptionId,
        );
        setTableData((state: any) => ({
          ...state,
          pageSize: data.perPage,
          pageIndex: data.toSkip / data.perPage,
        }));
      },
    },
  );

  const columns: ColumnDef<any, any>[] = [
    {
      header: t("profile"),
      accessorKey: "profile",
      enableColumnFilter: false,
    },
    {
      header: t("ID"),
      accessorKey: "fieldglassId",
      enableColumnFilter: true,
    },
    {
      header: t("name"),
      accessorKey: "activityItemDescription",
      enableColumnFilter: true,
    },
    {
      header: t("activity"),
      accessorKey: "activityItemCode",
      enableColumnFilter: true,
    },
    {
      header: t("status"),
      accessorKey: "activityItemStatus",
      enableColumnFilter: false,
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) =>
          t(text, { ns: "status" }),
        ),
    },
    {
      header: t("date"),
      accessorKey: "activityItemDueDate",
      enableColumnFilter: false,
    },
    {
      header: t("actions"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title="View">
            <ButtonTS
              onClick={() => redirectToProfile(row?.original)}
              round
              justIcon
              size="sm"
              color="info"
            >
              <ArrowForwardIosIcon />
            </ButtonTS>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  function redirectToProfile(row: any) {
    const match = row.activityItemDueDate.match(/(\d{2})\/(\d{2})\/(\d{4})/);

    const month = match ? match[2] : null;
    const year = match ? match[3] : null;

    if (isKuo && row?.profile !== ProfileEnum.WORKER) {
      return navigate(
        `../${WorkItemProfiles["SOWADM"]}/profile?id=${row.fieldglassId}&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}&activity=${row.type}`,
      );
    }

    navigate(
      `../${WorkItemProfiles[row.profile as never]}/profile?id=${
        row.fieldglassId
      }&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}&activity=${
        row.type
      }`,
    );
  }

  return (
    <>
      <GridItemTS xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Search />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>{t("last-pending")}</h4>
          </CardHeader>
          <CardBody>
            {isLoading || isFetching ? (
              <Skeleton />
            ) : (
              <PaginatedTable
                columns={columns}
                data={data?.result || []}
                tableState={tableData}
                pageCount={data?.totalPages}
                setTableState={setTableData}
                fixedPageSize
              />
            )}
          </CardBody>
        </Card>
      </GridItemTS>
    </>
  );
}
